import React from 'react';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Renders one of the three plan sections (milestones / events / tasks) as a
 * collapsible `<details>` block. Replaces three nearly-identical inline blocks
 * the wizard used to carry.
 */
function PlanSection({ title, items, defaultOpen, renderItem }) {
  if (!items || items.length === 0) return null;
  return (
    <details {...(defaultOpen ? { open: true } : {})} className="goal-plan-section">
      <summary>{title}</summary>
      <ul>
        {items.map((item, i) => (
          <li key={i}>{renderItem(item, i)}</li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Step 3 — review the generated plan and confirm / regenerate.
 *
 * Props:
 *   plan:               generated plan object (null = nothing to show)
 *   documentContext:    optional extracted context summary from attached docs
 *   attachedDocuments:  [{id, name}] (for the "Informed by your documents" header)
 *   loading:            boolean
 *   onBack:             () => void
 *   onRegenerate:       () => void
 *   onConfirm:          () => void
 */
export default function ReviewStep({
  plan,
  documentContext,
  attachedDocuments,
  loading,
  onBack,
  onRegenerate,
  onConfirm,
}) {
  if (!plan) return null;

  const events = plan.events || [];
  const assignments = plan.assignments || [];
  const milestones = assignments
    .filter((a) => a.isMilestone)
    .slice()
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  const tasks = assignments.filter((a) => !a.isMilestone);

  const docEvents = documentContext?.events || [];
  const docDeadlines = documentContext?.deadlines || [];
  const docBlackouts = documentContext?.blackoutDates || [];
  const docCount = (attachedDocuments || []).length;
  const hasDocContext = docEvents.length || docDeadlines.length || docBlackouts.length;

  return (
    <section className="goal-step">
      <h2>{plan.endeavor?.name || 'Your plan'}</h2>
      {plan.summary && <p className="goal-summary">{plan.summary}</p>}

      <div className="goal-plan-counts">
        <span>{milestones.length} milestones</span>
        <span>{events.length} check-ins</span>
        <span>{tasks.length} assignments</span>
        {docCount > 0 && <span>{docCount} documents</span>}
      </div>

      {hasDocContext ? (
        <PlanSection
          title={`Informed by your ${docCount} document${docCount === 1 ? '' : 's'}`}
          items={[
            ...docEvents.map((e) => ({ kind: 'event', ...e })),
            ...docDeadlines.map((d) => ({ kind: 'deadline', ...d })),
            ...docBlackouts.map((b) => ({ kind: 'blackout', ...b })),
          ]}
          renderItem={(it) => {
            if (it.kind === 'event') {
              return (
                <>
                  <span className="goal-chip">event</span>
                  <strong>{it.name}</strong>
                  {it.start && (
                    <span className="goal-muted"> · {formatDate(it.start)}</span>
                  )}
                </>
              );
            }
            if (it.kind === 'deadline') {
              return (
                <>
                  <span className="goal-chip">deadline</span>
                  <strong>{it.label}</strong>
                  {it.date && (
                    <span className="goal-muted"> · {formatDate(it.date)}</span>
                  )}
                </>
              );
            }
            return (
              <>
                <span className="goal-chip">blackout</span>
                <strong>{it.label}</strong>
                {it.start && (
                  <span className="goal-muted"> · {formatDate(it.start)}</span>
                )}
              </>
            );
          }}
        />
      ) : null}

      <PlanSection
        title="Milestones"
        items={milestones}
        defaultOpen
        renderItem={(m) => (
          <>
            <strong>{m.title}</strong>
            {m.dueDatetime && (
              <span className="goal-muted"> · {formatDate(m.dueDatetime)}</span>
            )}
            {m.description && <div className="goal-muted-block">{m.description}</div>}
          </>
        )}
      />

      <PlanSection
        title={`Check-in schedule (first 10 of ${events.length})`}
        items={events.slice(0, 10)}
        renderItem={(e) => (
          <>
            <strong>{e.name}</strong>
            {e.startDatetime && (
              <span className="goal-muted"> · {formatDate(e.startDatetime)}</span>
            )}
            {e.blueprint?.contentType && (
              <span className="goal-chip">{e.blueprint.contentType}</span>
            )}
          </>
        )}
      />

      <PlanSection
        title={`Assignments (${tasks.length})`}
        items={tasks}
        renderItem={(a) => (
          <>
            <strong>{a.title}</strong>
            {a.dueDatetime && (
              <span className="goal-muted"> · due {formatDate(a.dueDatetime)}</span>
            )}
          </>
        )}
      />

      <div className="goal-actions">
        <button type="button" className="goal-btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="goal-btn-secondary"
          onClick={onRegenerate}
          disabled={loading}
        >
          Regenerate
        </button>
        <button
          type="button"
          className="goal-btn-primary"
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Saving…' : 'Create plan'}
        </button>
      </div>
    </section>
  );
}
